import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { getToken, setToken, clearToken } from '../utils/tokenStorage';

const AuthContext = createContext(null);
const ADMIN_DASHBOARD_ROLES = ['super_admin', 'admin', 'accountant'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = getToken();
    if (token) {
      try {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await api.get('/auth/me');
        if (response.data.success && response.data.userType === 'admin') {
          const adminData = response.data.data;
          if (ADMIN_DASHBOARD_ROLES.includes(adminData?.role)) {
            setAdmin(adminData);
          } else {
            clearToken();
            delete api.defaults.headers.common['Authorization'];
          }
        } else {
          clearToken();
          delete api.defaults.headers.common['Authorization'];
        }
      } catch (error) {
        clearToken();
        delete api.defaults.headers.common['Authorization'];
      }
    }
    setLoading(false);
  };

  const login = async (email, password, remember = false) => {
    try {
      const response = await api.post('/auth/login/admin', { email, password });
      if (response.data.success) {
        const { token, admin: adminData } = response.data;
        if (!ADMIN_DASHBOARD_ROLES.includes(adminData?.role)) {
          return {
            success: false,
            message: 'Only admin accounts can access the admin dashboard'
          };
        }
        setToken(token, { persist: remember });
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setAdmin(adminData);
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        status: error.response?.status,
      };
    }
  };

  const logout = () => {
    clearToken();
    delete api.defaults.headers.common['Authorization'];
    setAdmin(null);
  };

  const value = {
    admin,
    loading,
    login,
    logout,
    isAuthenticated: !!admin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
